import { MelodyLib, NoteLib, ScaleLib } from '@tunasong/music-lib'
import type { Melody, MelodyGrid, Scale } from '@tunasong/schemas'

/**
 * Pure geometry for the melody piano roll. Everything is in percentages of a `viewBox 0 0 100 100`
 * SVG, the same convention `VoicingPianoRoll` uses, so the roll scales with its container and the
 * maths stays unit-free and testable.
 *
 * Pitch runs vertically with the HIGHEST pitch at the top, which is how a piano roll and a stave
 * both read. Beats run horizontally.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4.1
 */

export interface RollRange {
  /** Lowest MIDI number drawn. */
  low: number
  /** Highest MIDI number drawn. */
  high: number
}

/** Two octaves - enough that a short melody is not a single stripe. */
const MIN_SPAN = 24

/** Headroom above and below the melody, so the top and bottom notes are not on the edge. */
const PADDING = 2

/** C3-C5, centred on middle C: where a hand-entered melody usually starts. */
const EMPTY_RANGE: RollRange = { low: 48, high: 72 }

export const rollPitchRange = (melody: Melody): RollRange => {
  if (melody.notes.length === 0) {
    return EMPTY_RANGE
  }
  const midis = melody.notes.map(n => n.midi)
  const low = Math.min(...midis) - PADDING
  const high = Math.max(...midis) + PADDING
  const grow = Math.ceil(Math.max(0, MIN_SPAN - (high - low)) / 2)
  return { low: Math.max(0, low - grow), high: Math.min(127, high + grow) }
}

/**
 * How many beats the roll draws - always whole bars, always at least one. `minBeats` stretches it to
 * cover more than the notes: the chord lane's chords, so an empty melody still shows every bar of the
 * section it is written over (melody toolkit plan M1).
 */
export const rollBeats = (melody: Melody, minBeats = 0): number => {
  const perBar = MelodyLib.barBeats(melody.tempo.meter)
  const end = melody.notes.reduce((last, n) => Math.max(last, n.onset + n.duration), minBeats)
  return Math.max(perBar, Math.ceil(end / perBar) * perBar)
}

/**
 * Spec §10: with reduced motion the play head steps by beat instead of sliding. The position, held at the
 * start of the beat it is in; null (stopped) stays null.
 */
export const stepToBeat = (playheadMs: number | null, bpm: number): number | null => {
  if (playheadMs === null) {
    return null
  }
  const beat = MelodyLib.beatMs(bpm)
  return Math.floor(playheadMs / beat) * beat
}

/** Number of rows drawn - one per semitone, inclusive of both ends. */
export const rowCount = (range: RollRange): number => range.high - range.low + 1

export const rowHeight = (range: RollRange): number => 100 / rowCount(range)

/** Top edge of a pitch's row. 0 % is the highest pitch. */
export const rowY = (midi: number, range: RollRange): number => (range.high - midi) * rowHeight(range)

export const beatX = (beats: number, totalBeats: number): number => (beats / totalBeats) * 100

export const xToBeat = (xPercent: number, totalBeats: number): number => (xPercent / 100) * totalBeats

export const yToMidi = (yPercent: number, range: RollRange): number =>
  Math.min(range.high, Math.max(range.low, range.high - Math.floor(yPercent / rowHeight(range))))

/**
 * The slot a beat position falls INSIDE, as opposed to the nearest slot line.
 *
 * Click-to-insert wants the cell under the pointer: clicking three-quarters of the way through a
 * slot must insert in that slot, not in the next one. `MelodyLib.snapToSlot` rounds, which is
 * right for dragging a note and wrong for pointing at a cell.
 */
export const slotFloor = (beats: number, grid: MelodyGrid): number => {
  const slot = MelodyLib.gridSlotBeats(grid)
  return Math.max(0, Math.floor(beats / slot + MelodyLib.GRID_TOLERANCE) * slot)
}

/**
 * Which cell a pointer is over, given its position as a percentage of the plot.
 *
 * The roll draws no per-cell rects: a 16-bar melody on a 16th grid across a 40-semitone range
 * would be ten thousand of them. One transparent overlay carries the click, and this function
 * turns the position into a cell - which keeps the arithmetic testable without a layout engine.
 */
export const pointToCell = (
  xPercent: number,
  yPercent: number,
  range: RollRange,
  totalBeats: number,
  grid: MelodyGrid
): { beat: number; midi: number } => ({
  beat: slotFloor(xToBeat(xPercent, totalBeats), grid),
  midi: yToMidi(yPercent, range),
})

/**
 * `scaleSchema.type` is optional, and a key with no scale type is read as major - the melody's
 * `key` is a hint for shading, and a root with no quality is what a user picking "C" means.
 */
const keyChromas = (key: Scale): number[] =>
  ScaleLib.getNotesChromaticIndices({ root: key.root, type: key.type ?? 'major' })

/** Notes outside the key are never rejected or flagged (§2) - this only shades the row. */
export const isInKey = (midi: number, key?: Scale): boolean => (key ? keyChromas(key).includes(midi % 12) : true)

export const isTonic = (midi: number, key?: Scale): boolean =>
  key ? midi % 12 === NoteLib.getNoteChromaticIndex(key.root) : false
